import React, { useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, Box, Typography, TextField,
    Button, IconButton, InputAdornment, Alert, CircularProgress, Divider,
} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useDispatch, useSelector } from 'react-redux';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK } from '../theme';
import http, { apiErrorMessage } from '../Api/http';
import { PostUpdateProfile, PostChangeEmail } from '../Api/Api';
import { profileUpdated } from '../redux/slices/authSlice';

const field = {
    '& .MuiOutlinedInput-root': { borderRadius: '9px', fontSize: 13.5, bgcolor: '#fff' },
    '& .MuiInputLabel-root': { fontSize: 13.5 },
};

const isValidPhone = (raw = '') => {
    const digits = String(raw).replace(/\D/g, '');
    return !raw || (digits.length >= 10 && digits.length <= 15);
};

// One dialog for the three self-account-settings calls the handoff added:
// PostUpdateProfile (name/phone, no password, no logout) and PostChangeEmail
// (requires current password, ends every other session). Works the same for
// every account type, including MasterAdmin, which has no employee row of
// its own to edit elsewhere.
export default function ProfileDialog({ open, onClose, onEmailChanged }) {
    const dispatch = useDispatch();
    const auth = useSelector((s) => s.auth);

    const [name, setName] = useState(auth.userName || '');
    const [phone, setPhone] = useState(auth.user?.phone || '');
    const [profileTried, setProfileTried] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [profileDone, setProfileDone] = useState(false);

    const [newEmail, setNewEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [emailTried, setEmailTried] = useState(false);
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailDone, setEmailDone] = useState(false);

    const phoneInvalid = Boolean(phone) && !isValidPhone(phone);
    const profileValid = name.trim() && !phoneInvalid;

    const emailFormatOk = /\S+@\S+\.\S+/.test(newEmail.trim());
    const emailValid = newEmail.trim() && emailFormatOk
        && newEmail.trim().toLowerCase() !== (auth.email || '').toLowerCase() && currentPassword;

    const reset = () => {
        setName(auth.userName || ''); setPhone(auth.user?.phone || '');
        setProfileTried(false); setProfileError(''); setProfileSaving(false); setProfileDone(false);
        setNewEmail(''); setCurrentPassword(''); setShowPwd(false);
        setEmailTried(false); setEmailError(''); setEmailSaving(false); setEmailDone(false);
    };
    const close = () => { if (!profileSaving && !emailSaving) { reset(); onClose(); } };

    const saveProfile = async () => {
        setProfileTried(true);
        if (!profileValid) return;
        setProfileSaving(true); setProfileError(''); setProfileDone(false);
        try {
            const { data: body } = await http.post(PostUpdateProfile, {
                name: name.trim(),
                phone: phone.trim(),
            });
            if (body?.error) throw new Error(body.message || 'Could not update your profile.');
            dispatch(profileUpdated({ name: body?.data?.name ?? name.trim(), phone: body?.data?.phone ?? phone.trim() }));
            setProfileDone(true);
        } catch (err) {
            setProfileError(apiErrorMessage(err, 'Could not update your profile.'));
        } finally {
            setProfileSaving(false);
        }
    };

    const changeEmail = async () => {
        setEmailTried(true);
        if (!emailValid) return;
        setEmailSaving(true); setEmailError('');
        try {
            const { data: body } = await http.post(PostChangeEmail, {
                newEmail: newEmail.trim(),
                currentPassword,
            });
            if (body?.error) throw new Error(body.message || 'Could not change your email.');
            setEmailDone(true);
        } catch (err) {
            setEmailError(apiErrorMessage(err, 'Could not change your email.'));
        } finally {
            setEmailSaving(false);
        }
    };

    if (emailDone) {
        return (
            <Dialog open={open} onClose={() => {}} maxWidth="xs" fullWidth
                slotProps={{ paper: { sx: { borderRadius: '16px', p: 0.5 } } }}>
                <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
                    <Box sx={{
                        width: 62, height: 62, borderRadius: '50%', mx: 'auto', mb: 2,
                        bgcolor: '#DCFCE7', border: '1px solid #BBF7D0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 32, color: '#16A34A' }} />
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Email changed</Typography>
                    <Typography sx={{ fontSize: 13.5, color: '#6B7280', mt: 0.8, px: 1 }}>
                        For your security every signed-in device has been logged out.
                        Please sign in again with your new email.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
                    <Button
                        onClick={() => { reset(); onEmailChanged(); }}
                        fullWidth
                        sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: 14, height: 44,
                            borderRadius: '10px', color: '#fff', bgcolor: PRIMARY, boxShadow: 'none',
                            '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                        }}
                    >
                        Go to sign in
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={close} maxWidth="xs" fullWidth
            slotProps={{ paper: { sx: { borderRadius: '16px', p: 0.5 } } }}>
            <DialogContent sx={{ pt: 3, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 2.2 }}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '11px',
                        bgcolor: PRIMARY_LIGHT, color: PRIMARY,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <PersonRoundedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: '#0F172A' }}>My Profile</Typography>
                        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>Update your name, phone and sign-in email.</Typography>
                    </Box>
                </Box>

                {profileError && <Alert severity="error" sx={{ mb: 1.6, borderRadius: '9px', fontSize: 12.5, py: 0.4 }}>{profileError}</Alert>}
                {profileDone && <Alert severity="success" sx={{ mb: 1.6, borderRadius: '9px', fontSize: 12.5, py: 0.4 }}>Profile updated.</Alert>}

                <TextField
                    label="Name" value={name}
                    onChange={(e) => { setName(e.target.value); setProfileDone(false); }}
                    size="small" fullWidth
                    error={profileTried && !name.trim()}
                    helperText={profileTried && !name.trim() ? 'Enter your name' : ' '}
                    sx={field}
                />
                <TextField
                    label="Phone (optional)" value={phone}
                    onChange={(e) => { setPhone(e.target.value.replace(/[^\d+\s-]/g, '')); setProfileDone(false); }}
                    size="small" fullWidth
                    error={phoneInvalid}
                    helperText={phoneInvalid ? 'Enter a valid 10–15 digit phone number' : ' '}
                    sx={field}
                />
                <Button
                    onClick={saveProfile} disabled={profileSaving}
                    startIcon={profileSaving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                    sx={{
                        mt: -0.6, mb: 2, textTransform: 'none', fontWeight: 700, fontSize: 13, height: 38,
                        borderRadius: '9px', color: '#fff', bgcolor: PRIMARY, boxShadow: 'none',
                        '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                    }}
                >
                    {profileSaving ? 'Saving…' : 'Save profile'}
                </Button>

                <Divider sx={{ mb: 2 }} />

                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A', mb: 0.3 }}>Sign-in email</Typography>
                <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 1.4 }}>
                    Currently <b>{auth.email || '—'}</b>. Changing it signs you out everywhere.
                </Typography>

                {emailError && <Alert severity="error" sx={{ mb: 1.6, borderRadius: '9px', fontSize: 12.5, py: 0.4 }}>{emailError}</Alert>}

                <TextField
                    label="New email" type="email" value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    size="small" fullWidth autoComplete="email"
                    error={emailTried && (!newEmail.trim() || !emailFormatOk)}
                    helperText={emailTried && !newEmail.trim() ? 'Enter a new email' : ' '}
                    sx={field}
                />
                <TextField
                    label="Current password" type={showPwd ? 'text' : 'password'} value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && emailValid && !emailSaving) changeEmail(); }}
                    size="small" fullWidth autoComplete="current-password"
                    error={emailTried && !currentPassword}
                    helperText={emailTried && !currentPassword ? 'Enter your current password' : ' '}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPwd((v) => !v)} edge="end" size="small" tabIndex={-1}>
                                        {showPwd ? <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={field}
                />
                <Button
                    onClick={changeEmail} disabled={emailSaving}
                    startIcon={emailSaving ? <CircularProgress size={15} sx={{ color: PRIMARY }} /> : <AlternateEmailRoundedIcon sx={{ fontSize: 17 }} />}
                    sx={{
                        mt: -0.6, textTransform: 'none', fontWeight: 700, fontSize: 13, height: 38,
                        borderRadius: '9px', color: PRIMARY, bgcolor: PRIMARY_LIGHT, boxShadow: 'none',
                        '&:hover': { bgcolor: '#D0E5F3' },
                    }}
                >
                    {emailSaving ? 'Changing…' : 'Change email'}
                </Button>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.4 }}>
                <Button
                    onClick={close}
                    disabled={profileSaving || emailSaving}
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: 14, height: 44, borderRadius: '10px', color: '#334155', bgcolor: '#F1F5F9', boxShadow: 'none', '&:hover': { bgcolor: '#E2E8F0', boxShadow: 'none' } }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
